
import { Box, Dialog, DialogContent, MenuItem, TextField, Typography } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import CheckboxFormControl from "components/CheckboxFormControl"
import ComboBox from "components/ComboBox"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { initTyreDialogData } from "./initData"
import { tyreApiNm, tyreColNm, tyreStsNm } from "./constant"
import { tyreTransactionColumns } from "./columns"
import { alertConfirmDelete } from "components/Alert"
import { bahtProps } from "utils/bahtProps"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import dayjs from "dayjs"
import FilePreviewCard from "components/FileUplaod/FilePreviewCard"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import AddButton from "components/buttons/AddButton"
import TyreTransactionDialog from "./TyreTransactionDialog"
import { addIdForDataGrid } from "utils"

let selectedTrnsId = null

const TyreDialog = ({ selectedId, dialogOpen, setDialogOpen, onFinish, tyreSizeCombo }) => {

  const { ax, msData } = useContext(GlobalStateContext)
  const [dialogData, setDialogData] = useState({ ...initTyreDialogData })
  const [origData, setOrigData] = useState({ ...initTyreDialogData })
  const [filesData, setFilesData] = useState([])
  const [pendingFiles, setPendingFiles] = useState([])
  const [transData, setTransData] = useState([])
  const [trnsDialogOpen, setTrnsDialogOpen] = useState(false)

  const dialogDataProp = useCallback((name) => ({
    fullWidth: true,
    size: "small",
    label: tyreColNm[name],
    value: dialogData[name] ?? "",
    onChange: (e) => setDialogData(o => ({ ...o, [name]: e.target.value })),
  }), [dialogData])

  const uploadFiles = useCallback(async (tyrId) => {
    for (const pf of pendingFiles) {
      const formData = new FormData()
      formData.append('file', pf.file)
      formData.append('TyrId', tyrId)
      formData.append('Label', pf.name)
      await ax.post(tyreApiNm.uploadTyreFile, formData)
    }
    setPendingFiles([])
  }, [ax, pendingFiles])

  const loadTransactions = useCallback((tyrId) => {
    ax.post(tyreApiNm.getTyreTransaction, { TyrId: tyrId }).then(value => {
      if (value.data) {
        setTransData(addIdForDataGrid(value.data, 'TyrTrnsId'))
      }
    })
  }, [ax])

  const loadDetail = useCallback((tyrId) => {
    ax.post(tyreApiNm.getTyreDetail, { TyrId: tyrId }).then(value => {
      if (value.data && value.data.length > 0) {
        setDialogData(value.data[0])
        setOrigData(value.data[0])
        setFilesData(value.data[0].Files || [])
      }
    })
  }, [ax])

  const handleDialogOk = useCallback(async () => {
    if (selectedId) {
      const colValues = {}
      for (const key of ['IsActv', 'TyrCd', 'TyrBrand', 'TyrModel', 'TyrSzId', 'PurchPr', 'PurchDte', 'SupId', 'TyrStsId', 'TukId', 'TlrId', 'TyrPos', 'Mileage', 'Dscp', 'Rmk']) {
        if (String(dialogData[key] ?? '') !== String(origData[key] ?? '')) {
          colValues[key] = dialogData[key]
        }
      }
      const hasChange = Object.keys(colValues).length > 0
      const hasNewFiles = pendingFiles.length > 0
      if (hasChange) {
        await ax.post(tyreApiNm.updateTyreColumn, {
          TyrId: selectedId,
          ColValues: colValues,
        })
      }
      if (hasNewFiles) {
        await uploadFiles(selectedId)
      }
      if (hasChange || hasNewFiles) {
        onFinish()
      }
      setDialogOpen(false)
    } else {
      const postData = { ...dialogData }
      const value = await ax.post(tyreApiNm.insertTyre, postData)
      if (value.data) {
        const tyrId = value.data
        if (tyrId && pendingFiles.length > 0) {
          await uploadFiles(tyrId)
        }
        onFinish()
        setDialogOpen(false)
      }
    }
  }, [ax, dialogData, origData, onFinish, setDialogOpen, selectedId, pendingFiles, uploadFiles])

  const handleDelete = useCallback(() => {
    alertConfirmDelete(() => {
      ax.post(tyreApiNm.deleteTyre, { TyrId: selectedId }).then(value => {
        if (value.data) {
          onFinish()
          setDialogOpen(false)
        }
      })
    })
  }, [ax, onFinish, selectedId, setDialogOpen])

  const handleDeleteServerFile = useCallback((file) => {
    return new Promise((resolve) => {
      alertConfirmDelete(() => {
        ax.post(tyreApiNm.deleteTyreFile, { TyrFileId: file.TyrFileId }).then(value => {
          if (value.data) {
            resolve()
          }
        })
      })
    })
  }, [ax])

  const handleAddTransaction = useCallback(() => {
    selectedTrnsId = null
    setTrnsDialogOpen(true)
  }, [])

  const handleTrnsRowDoubleClick = useCallback((params) => {
    selectedTrnsId = params.id
    setTrnsDialogOpen(true)
  }, [])

  const handleTransactionFinish = useCallback(() => {
    loadTransactions(selectedId)
    loadDetail(selectedId)
    onFinish()
  }, [loadTransactions, loadDetail, selectedId, onFinish])

  useEffect(() => {
    if (dialogOpen) {
      if (selectedId) {
        loadDetail(selectedId)
        loadTransactions(selectedId)
      } else {
        setFilesData([])
        setTransData([])
      }
    } else {
      setDialogData({ ...initTyreDialogData })
      setOrigData({ ...initTyreDialogData })
      setFilesData([])
      setTransData([])
      setPendingFiles(o => {
        o.forEach(f => URL.revokeObjectURL(f.preview))
        return []
      })
    }
  }, [dialogOpen, ax, selectedId, loadDetail, loadTransactions])

  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth={selectedId ? 'xl' : 'xs'}
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title="ข้อมูลยาง"
        onDeleteClick={selectedId ? handleDelete : null}
      />
      <DialogContent>
        <BoxFR pt={2} sx={{ gap: 2, alignItems: 'flex-start' }}>
          {/* Left: Tyre master fields */}
          <BoxFC sx={{ gap: 1, minWidth: 300 }}>
            <CheckboxFormControl label={"Active"}
              checked={dialogData.IsActv === 1}
              onChange={e => setDialogData(o => ({ ...o, IsActv: e.target.checked ? 1 : 0 }))} />
            <TextField {...dialogDataProp('TyrCd')} />
            <BoxFR sx={{ gap: 1 }}>
              <TextField {...dialogDataProp('TyrBrand')} />
              <TextField {...dialogDataProp('TyrModel')} />
            </BoxFR>
            <ComboBox
              options={tyreSizeCombo || []}
              label={tyreColNm.TyrSzId}
              selectedId={dialogData.TyrSzId}
              setSelectedId={(id) => setDialogData(o => ({ ...o, TyrSzId: id }))}
            />
            <BoxFR sx={{ gap: 1 }}>
              <TextField {...dialogDataProp('PurchPr')} type="number" {...bahtProps} />
              <DesktopDatePicker
                label={tyreColNm.PurchDte}
                inputFormat="DD/MM/YYYY"
                value={dialogData.PurchDte}
                onChange={(v) => setDialogData(o => ({ ...o, PurchDte: v ? dayjs(v).format("YYYY-MM-DD") : null }))}
                renderInput={(params) => <TextField size="small" fullWidth {...params} />}
              />
            </BoxFR>
            <ComboBox
              options={msData.supplierCombo || []}
              label={tyreColNm.SupId}
              selectedId={dialogData.SupId}
              setSelectedId={(id) => setDialogData(o => ({ ...o, SupId: id }))}
            />
            <TextField {...dialogDataProp('TyrStsId')} select disabled={!!selectedId}>
              {Object.entries(tyreStsNm).map(([key, label]) => (
                <MenuItem key={key} value={+key}>{label}</MenuItem>
              ))}
            </TextField>
            <BoxFR sx={{ gap: 1 }}>
              <ComboBox
                options={msData.truckCombo || []}
                label={tyreColNm.TukId}
                selectedId={dialogData.TukId}
                setSelectedId={(id) => setDialogData(o => ({ ...o, TukId: id }))}
                disabled={!!selectedId}
              />
              <ComboBox
                options={msData.trailerCombo || []}
                label={tyreColNm.TlrId}
                selectedId={dialogData.TlrId}
                setSelectedId={(id) => setDialogData(o => ({ ...o, TlrId: id }))}
                disabled={!!selectedId}
              />
            </BoxFR>
            <BoxFR sx={{ gap: 1 }}>
              <TextField {...dialogDataProp('TyrPos')} disabled={!!selectedId} />
              <TextField {...dialogDataProp('Mileage')} type="number" disabled={!!selectedId}
                label={tyreColNm.Mileage} />
            </BoxFR>
            <TextField {...dialogDataProp('Dscp')} multiline rows={2} />
            <TextField {...dialogDataProp('Rmk')} />
            <FilePreviewCard
              filesData={filesData}
              setFilesData={setFilesData}
              pendingFiles={pendingFiles}
              setPendingFiles={setPendingFiles}
              onDeleteServerFile={handleDeleteServerFile}
              fileIdKey="TyrFileId"
            />
          </BoxFC>

          {/* Right: Transaction history (only when editing) */}
          {selectedId && (
            <BoxFC sx={{ flex: 1, gap: 1, minWidth: 400, height: 500 }}>
              <BoxFR>
                <Typography variant="subtitle1" fontWeight="bold">ประวัติรายการ</Typography>
                <Box flex={1} />
                <AddButton onClick={handleAddTransaction}>เพิ่มรายการ</AddButton>
              </BoxFR>
              <Box flex={1}>
                <DataGridCellExpand
                  hideFooter
                  rows={transData}
                  columns={tyreTransactionColumns}
                  onRowDoubleClick={handleTrnsRowDoubleClick}
                />
              </Box>
              <TyreTransactionDialog
                selectedId={selectedTrnsId}
                tyrId={selectedId}
                dialogOpen={trnsDialogOpen}
                setDialogOpen={setTrnsDialogOpen}
                onFinish={handleTransactionFinish}
              />
            </BoxFC>
          )}
        </BoxFR>
      </DialogContent>
      <DialogFooter okText="บันทึก"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleDialogOk} />
    </Dialog>
  )
}

export default React.memo(TyreDialog)
